import { FitAddon } from "@xterm/addon-fit";
import { Terminal } from "@xterm/xterm";
import "@xterm/xterm/css/xterm.css";
import { renderObserverProjection, TerminalConnectionRuntime } from "@orni/terminal-runtime";
import { ArrowDown, ArrowUp, Command, CornerDownLeft, Copy, ImagePlus, Keyboard, Lightbulb, PanelRightClose, PanelRightOpen, Pause, Play, RotateCcw, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import type { DesktopClient } from "@orni/domain-typescript";
import {
  nativeTerminalLifecycleFromSession,
  reduceNativeTerminalLifecycle,
  type NativeTerminalLifecycleEvent,
} from "@orni/sdk-typescript";
import type { Session, TerminalPrompt } from "../domain";
import type { ControllerState, TerminalConnection, TerminalScreenBootstrap } from "../sdk/types";
import type { ProviderTerminalQuickCommand } from "../sdk/types";
import { localizeError, useI18n } from "../i18n";
import { terminalControlAction } from "../terminal-control-policy";

interface TerminalSurfaceProps {
  client: DesktopClient;
  session: Session;
  active?: boolean;
  readOnly?: boolean;
  onOpenNotes?: () => void;
  toolsExpanded?: boolean;
  onToggleTools?: () => void;
  /** Desktop Session shell owns actions; keep the PTY surface terminal-only. */
  minimalChrome?: boolean;
  onPrompt?: (prompt: TerminalPrompt | null) => void;
  promptAnswer?: { promptId: string; optionId: string; nonce: number } | null;
  insertText?: { text: string; nonce: number } | null;
  /** Adapter-verified commands for the active Provider's native composer. */
  nativeQuickCommands?: readonly ProviderTerminalQuickCommand[];
}

type TerminalControlNotice = "idle" | "acquiring" | "acquired";

const MAX_PAUSED_BYTES = 4 * 1024 * 1024;
const MAX_PENDING_INPUT_BYTES = 256 * 1024;
const MAX_TERMINAL_RECONNECT_ATTEMPTS = 8;
const STABLE_CONNECTION_MS = 10_000;
const DEFAULT_FONT_SIZE = 12;
const DEFAULT_LINE_HEIGHT = 1.2;
const INITIAL_SNAPSHOT_TIMEOUT_MS = 15_000;
const inputEncoder = new TextEncoder();
interface SavedTerminalViewport {
  viewportY: number;
  offsetFromBottom: number;
  follow: boolean;
}

// Keep the optional prompt callback stable when callers do not provide one.
// Terminal connection setup depends on this callback; creating a new default
// function during every render would reconnect the PTY on unrelated layout
// updates such as collapsing the desktop session list.
const noopPrompt = (_prompt: TerminalPrompt | null): void => undefined;

const savedTerminalViewports = new Map<string, SavedTerminalViewport>();
const MAX_SAVED_TERMINAL_VIEWPORTS = 100;

function saveTerminalViewport(sessionId: string, terminal: Terminal, follow: boolean): void {
  savedTerminalViewports.delete(sessionId);
  savedTerminalViewports.set(sessionId, {
    viewportY: terminal.buffer.active.viewportY,
    offsetFromBottom: Math.max(0, terminal.buffer.active.baseY - terminal.buffer.active.viewportY),
    follow,
  });
  while (savedTerminalViewports.size > MAX_SAVED_TERMINAL_VIEWPORTS) {
    const oldest = savedTerminalViewports.keys().next().value;
    if (oldest === undefined) break;
    savedTerminalViewports.delete(oldest);
  }
}
export function TerminalSurface({ client, session, active = true, readOnly = false, onOpenNotes = () => undefined, toolsExpanded = false, onToggleTools = () => undefined, minimalChrome = false, onPrompt = noopPrompt, promptAnswer, insertText, nativeQuickCommands = [] }: TerminalSurfaceProps) {
  const { t } = useI18n();
  const hostRef = useRef<HTMLDivElement>(null);
  const terminalRef = useRef<Terminal | null>(null);
  // Canonical VT parsing is kept separate from the observer-local renderer.
  const canonicalTerminalRef = useRef<Terminal | null>(null);
  const latestSnapshotRef = useRef<TerminalScreenBootstrap | null>(null);
  const connectionRef = useRef<TerminalConnection | null>(null);
  const lastInsertNonceRef = useRef<number | null>(null);
  const pausedRef = useRef(false);
  const followRef = useRef(true);
  const pausedChunksRef = useRef<Uint8Array[]>([]);
  const pausedBytesRef = useRef(0);
  const enqueueTerminalWriteRef = useRef<(bytes: Uint8Array, after?: () => void) => void>(() => undefined);
  const pendingInputRef = useRef<Uint8Array[]>([]);
  const pendingInputBytesRef = useRef(0);
  const controlRequestRef = useRef<Promise<void> | null>(null);
  const controlNoticeTimerRef = useRef<number | null>(null);
  const [paused, setPaused] = useState(false);
  const [follow, setFollow] = useState(true);
  const lifecycleSeed = nativeTerminalLifecycleFromSession({
    id: session.id,
    status: desktopNativeSessionStatus(session.state),
    processAttemptId: session.processAttemptId ?? null,
    generation: session.generation ?? 1,
  });
  const lifecycleRef = useRef(lifecycleSeed);
  const [lifecycle, setLifecycle] = useState(lifecycleSeed);
  const emitLifecycle = (event: NativeTerminalLifecycleEvent): void => {
    const next = reduceNativeTerminalLifecycle(lifecycleRef.current, event);
    lifecycleRef.current = next;
    setLifecycle(next);
  };
  const status = desktopTerminalStatus(lifecycle);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [error, setError] = useState("");
  const [inputError, setInputError] = useState<string | null>(null);
  const [controlNotice, setControlNotice] = useState<TerminalControlNotice>("idle");
  const [applicationCursor, setApplicationCursor] = useState(false);
  const connectionRuntimeRef = useRef<TerminalConnectionRuntime<TerminalConnection> | null>(null);
  const [keypadOpen, setKeypadOpen] = useState(false);
  const [quickCommandsOpen, setQuickCommandsOpen] = useState(false);
  const attachmentInputRef = useRef<HTMLInputElement>(null);
  const attachmentPreviewRef = useRef<string | null>(null);
  const [terminalAttachment, setTerminalAttachment] = useState<{
    fileName: string;
    previewUrl: string;
    path: string | null;
  } | null>(null);
  const [attachmentUploading, setAttachmentUploading] = useState(false);
  const [attachmentProgress, setAttachmentProgress] = useState(0);
  const [attachmentError, setAttachmentError] = useState<string | null>(null);
  const [controller, setController] = useState<ControllerState>({
    mode: "unavailable",
    holderLabel: null,
    leaseId: null,
    expiresAt: null,
    generation: 1,
  });
  useEffect(() => {
    if (lifecycleRef.current.target.sessionId !== session.id) {
      lifecycleRef.current = lifecycleSeed;
      setLifecycle(lifecycleSeed);
      return;
    }
    emitLifecycle({
      type: "projection",
      sessionId: session.id,
      status: desktopNativeSessionStatus(session.state),
      processAttemptId: session.processAttemptId ?? null,
      generation: session.generation ?? 1,
    });
  }, [session.generation, session.id, session.processAttemptId, session.state]);
  useEffect(() => {
    pausedRef.current = paused;
    if (paused) return;
    const terminal = terminalRef.current;
    if (!terminal) return;
    const chunks = pausedChunksRef.current.splice(0);
    pausedBytesRef.current = 0;
    for (const chunk of chunks) enqueueTerminalWriteRef.current(chunk);
    if (followRef.current) terminal.scrollToBottom();
  }, [paused]);

  useEffect(() => {
    followRef.current = follow;
    if (follow) terminalRef.current?.scrollToBottom();
  }, [follow]);

  useEffect(() => {
    if (!active || window.matchMedia("(pointer: coarse)").matches) return;
    if (!readOnly && controller.mode !== "owned") return;
    const frame = window.requestAnimationFrame(() => terminalRef.current?.focus());
    return () => window.cancelAnimationFrame(frame);
  }, [active, controller.mode, readOnly, session.id]);

  // A queued keystroke belongs to one Session. Do not let a terminal view
  // switch deliver text typed for the previous Session to the next one.
  useEffect(() => () => {
    pendingInputRef.current = [];
    pendingInputBytesRef.current = 0;
    if (controlNoticeTimerRef.current !== null) {
      window.clearTimeout(controlNoticeTimerRef.current);
      controlNoticeTimerRef.current = null;
    }
  }, [session.id]);

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;
    const savedViewport = savedTerminalViewports.get(session.id);
    followRef.current = savedViewport?.follow ?? true;
    setFollow(followRef.current);
    let cancelled = false;
    let attachedConnection: TerminalConnection | null = null;
    let unsubscribeData = () => {};
    let unsubscribeSnapshot = () => {};
    let unsubscribePrompt = () => {};
    let unsubscribeConnectionClose = () => {};
    let unsubscribeControl = () => {};
    let previousControlMode: ControllerState["mode"] = "unavailable";
    let lastSnapshotWatermark = -1;
    let lastSnapshotShape = "";
    let applyingBootstrap = true;
    let resizeTimer: number | null = null;
    let lastSentSize: { rows: number; columns: number } | null = null;
    let projectionFrame: number | null = null;
    let projectionCallbacks: Array<() => void> = [];
    let projectedCanonicalShape: string | null = null;
    let settledFitFrame: number | null = null;
    // The desktop PTY grid is negotiated once for this mounted Session. A
    // sidebar toggle changes only the outer layout; it must not refit the
    // renderer, resize the shared PTY, or replay the terminal buffer.
    let initialRendererDimensions: { rows: number; cols: number } | null = null;
    let rendererGeometryLocked = false;
    // xterm writes are asynchronous. A reconnect snapshot must not reset or
    // resize the parser while an older frame is still queued, otherwise the
    // old frame can land after the new bootstrap and overwrite its last rows
    // or leave the cursor at the old position. Keep every screen mutation in
    // one FIFO so reset -> bootstrap -> live output is deterministic.
    let terminalWriteChain: Promise<void> = Promise.resolve();
    const cancelScheduledProjection = (): void => {
      if (projectionFrame !== null) {
        window.cancelAnimationFrame(projectionFrame);
        projectionFrame = null;
      }
      projectionCallbacks = [];
    };
    const renderProjectionNow = (preserveScrollOffset?: number, after?: () => void): void => {
      cancelScheduledProjection();
      const canonical = canonicalTerminalRef.current;
      const display = terminalRef.current;
      if (!canonical || !display) {
        after?.();
        return;
      }
      renderObserverProjection(canonical, display, {
        preserveScrollOffset,
        onComplete: () => {
          projectedCanonicalShape = `${canonical.buffer.active.type}:${canonical.buffer.active.baseY}:${canonical.buffer.active.viewportY}:${canonical.rows}:${canonical.cols}`;
          after?.();
        },
      });
    };
    const scheduleProjection = (after?: () => void): void => {
      if (after) projectionCallbacks.push(after);
      if (projectionFrame !== null) return;
      projectionFrame = window.requestAnimationFrame(() => {
        projectionFrame = null;
        const callbacks = projectionCallbacks;
        projectionCallbacks = [];
        const canonical = canonicalTerminalRef.current;
        const display = terminalRef.current;
        if (!canonical || !display) {
          callbacks.forEach((callback) => callback());
          return;
        }
        renderObserverProjection(canonical, display, {
          onComplete: () => {
            projectedCanonicalShape = `${canonical.buffer.active.type}:${canonical.buffer.active.baseY}:${canonical.buffer.active.viewportY}:${canonical.rows}:${canonical.cols}`;
            callbacks.forEach((callback) => callback());
          },
        });
      });
    };
    const writeLiveProjection = (bytes: Uint8Array, after?: () => void): void => {
      const canonical = canonicalTerminalRef.current;
      const display = terminalRef.current;
      if (!canonical || !display) {
        after?.();
        return;
      }
      // Desktop normally negotiates the same grid as the host. Keep the raw
      // VT stream in that case: it preserves the real cursor and avoids a
      // full-screen reset for every echoed character.
      if (canonical.cols === display.cols
        && canonical.rows === display.rows
        && canonical.buffer.active.type === "normal"
        && display.buffer.active.type === "normal") {
        display.write(bytes, () => {
          projectedCanonicalShape = `${canonical.buffer.active.type}:${canonical.buffer.active.baseY}:${canonical.buffer.active.viewportY}:${canonical.rows}:${canonical.cols}`;
          after?.();
        });
        return;
      }
      const nextShape = `${canonical.buffer.active.type}:${canonical.buffer.active.baseY}:${canonical.buffer.active.viewportY}:${canonical.rows}:${canonical.cols}`;
      // A changed base/viewport means scrollback structure changed and needs a
      // complete projection. Otherwise update the visible screen in place;
      // this keeps mobile/narrow observers stable while a user types.
      if (projectedCanonicalShape === null
        || projectedCanonicalShape.split(":").slice(0, 2).join(":") !== nextShape.split(":").slice(0, 2).join(":")) {
        scheduleProjection(after);
      } else {
        renderObserverProjection(canonical, display, { incremental: true, onComplete: after });
      }
    };
    const queueTerminalWrite = (bytes: Uint8Array, after?: () => void): void => {
      terminalWriteChain = terminalWriteChain.then(() => new Promise<void>((resolve) => {
        if (cancelled || !terminalRef.current || !canonicalTerminalRef.current) {
          resolve();
          return;
        }
        try {
          const canonical = canonicalTerminalRef.current;
          canonical.write(bytes, () => {
            try {
              writeLiveProjection(bytes, after);
            } finally {
              resolve();
            }
          });
        } catch {
          resolve();
        }
      })).catch(() => undefined);
    };
    enqueueTerminalWriteRef.current = queueTerminalWrite;
    const lockInitialRendererGeometry = (): boolean => {
      if (rendererGeometryLocked) return true;
      if (host.clientWidth < 20 || host.clientHeight < 20) return false;
      try { fit.fit(); } catch { fitTerminalGlyphsToHost(host, terminal); }
      const proposed = fit.proposeDimensions();
      initialRendererDimensions = proposed && proposed.rows > 0 && proposed.cols > 0
        ? { rows: proposed.rows, cols: proposed.cols }
        : { rows: terminal.rows, cols: terminal.cols };
      rendererGeometryLocked = true;
      return true;
    };
    const scheduleWritableResize = () => {
      if (resizeTimer !== null) window.clearTimeout(resizeTimer);
      lockInitialRendererGeometry();
      resizeTimer = window.setTimeout(() => {
        resizeTimer = null;
        const currentConnection = attachedConnection;
        if (!currentConnection?.writable || cancelled) return;
        const proposed = initialRendererDimensions;
        if (!proposed) return;
        const rows = proposed.rows;
        const columns = proposed.cols;
        if (rows < 1 || columns < 1) return;
        if (lastSentSize?.rows === rows && lastSentSize.columns === columns) return;
        lastSentSize = { rows, columns };
        currentConnection.resize(rows, columns);
      }, 100);
    };
    onPrompt?.(null);

    const terminal = new Terminal({
      allowTransparency: false,
      convertEol: false,
      cursorBlink: false,
      disableStdin: readOnly,
      fontFamily: '"SFMono-Regular", Menlo, Monaco, Consolas, monospace',
      fontSize: DEFAULT_FONT_SIZE,
      letterSpacing: 0,
      lineHeight: DEFAULT_LINE_HEIGHT,
      scrollback: 10_000,
      theme: {
        background: "#0a0c0e",
        foreground: "#edeef0",
        cursor: "#93aeff",
        cursorAccent: "#0a0c0e",
        selectionBackground: "#2b3651",
        black: "#0a0c0e",
        red: "#e0707a",
        green: "#6fcf97",
        yellow: "#e8b563",
        blue: "#7c9cff",
        magenta: "#c4a7ff",
        cyan: "#75d4d0",
        white: "#edeef0",
        brightBlack: "#5c636c",
        brightRed: "#f08991",
        brightGreen: "#8ce1ac",
        brightYellow: "#f0ca83",
        brightBlue: "#93aeff",
        brightMagenta: "#d7c2ff",
        brightCyan: "#98e5e1",
        brightWhite: "#ffffff",
      },
    });
    const fit = new FitAddon();
    terminal.loadAddon(fit);
    terminal.open(host);
    const canonical = new Terminal({
      cols: 80,
      rows: 24,
      allowTransparency: false,
      convertEol: false,
      disableStdin: true,
      scrollback: 10_000,
      theme: terminal.options.theme,
    });
    terminalRef.current = terminal;
    canonicalTerminalRef.current = canonical;

    const fitLocalRenderer = (resizeController: boolean): void => {
      // Fit only this observer's renderer. The hidden parser retains the
      // Session Host dimensions and is never changed by local layout.
      const run = () => {
        if (cancelled || host.clientWidth < 20 || host.clientHeight < 20) return;
        if (!rendererGeometryLocked) lockInitialRendererGeometry();
        terminal.refresh(0, Math.max(0, terminal.rows - 1));
        if (followRef.current) scrollTerminalSurfaceToBottom(terminal);
        if (resizeController) scheduleWritableResize();
      };

      run();
      // Font metrics can settle after the first layout pass. Re-fitting once
      // on the next frame prevents a stale row/column count from leaving
      // unused stage space or clipping the final rows. This never replays VT.
      if (settledFitFrame !== null) window.cancelAnimationFrame(settledFitFrame);
      settledFitFrame = window.requestAnimationFrame(() => {
        settledFitFrame = null;
        run();
      });
    };

    const fitTerminal = () => {
      if (rendererGeometryLocked) return;
      if (host.clientWidth < 20 || host.clientHeight < 20) return;
      lockInitialRendererGeometry();
      fitLocalRenderer(false);
    };

    const applySnapshot = (
      snapshot: TerminalScreenBootstrap,
      resetHistory: boolean,
    ) => {
      const currentBuffer = terminal.buffer.active;
      const currentOffsetFromBottom = Math.max(0, currentBuffer.baseY - currentBuffer.viewportY);
      const savedOffsetFromBottom = savedViewport?.follow === false
        ? Math.max(0, savedViewport.offsetFromBottom ?? 0)
        : 0;
      const restoreOffsetFromBottom = !followRef.current
        ? Math.max(currentOffsetFromBottom, savedOffsetFromBottom)
        : 0;
      const shape = `${snapshot.rows}:${snapshot.columns}:${snapshot.alternateScreen ? 1 : 0}`;
      // A resync snapshot may arrive after the binary frames it represents. Do
      // not replay an identical or older screen into xterm, which looks like a
      // page refresh and can duplicate the visible prompt.
      if (!resetHistory && (
        snapshot.watermark < lastSnapshotWatermark
        || (snapshot.watermark === lastSnapshotWatermark && shape === lastSnapshotShape)
      )) return;
      lastSnapshotWatermark = snapshot.watermark;
      lastSnapshotShape = shape;
      latestSnapshotRef.current = snapshot;
      setApplicationCursor(snapshot.applicationCursor);
      terminalWriteChain = terminalWriteChain.then(() => new Promise<void>((resolve) => {
        if (cancelled || !terminalRef.current || !canonicalTerminalRef.current) {
          resolve();
          return;
        }
        const display = terminalRef.current;
        const canonical = canonicalTerminalRef.current;
        if (resetHistory) applyingBootstrap = true;
        // A snapshot is a complete VT replacement. Reset only the hidden
        // parser so stale scrollback cannot leak into the next projection.
        canonical.reset();
        // The VT bootstrap was generated at the PTY's canonical dimensions.
        // Restore that grid only after older writes have drained; doing it
        // synchronously here lets a queued old frame use the new cursor grid
        // and corrupt the bottom of the screen.
        canonical.resize(snapshot.columns, snapshot.rows);
        lockInitialRendererGeometry();
        const finish = () => {
          if (cancelled) {
            resolve();
            return;
          }
          try {
            // xterm has finished applying the canonical VT bootstrap. Keep the
            // parser grid and native glyph metrics unchanged while settling the
            // local viewport.
            if (resetHistory) applyingBootstrap = false;
            if (resetHistory && restoreOffsetFromBottom > 0) {
              display.scrollToLine(Math.max(0, display.buffer.active.baseY - restoreOffsetFromBottom));
            } else if (followRef.current) {
              scrollTerminalSurfaceToBottom(display);
            }
          } finally {
            resolve();
          }
        };
        const writeScreen = () => canonical.write(snapshot.data, () => {
          renderProjectionNow(restoreOffsetFromBottom, finish);
        });
        const writeHistory = () => {
          const history = resetHistory ? snapshot.historyData : undefined;
          if (history?.byteLength) canonical.write(history, writeScreen);
          else writeScreen();
        };
        // History and the current screen are one continuous VT bootstrap.
        // Never paint a second text-only history layer over this renderer.
        writeHistory();
      })).catch(() => undefined);
    };

    const frame = window.requestAnimationFrame(fitTerminal);
    const resizeObserver = new ResizeObserver(fitTerminal);
    resizeObserver.observe(host);
    const captureScrollPosition = () => {
      if (applyingBootstrap) return;
      const buffer = terminal.buffer.active;
      const bufferAtBottom = buffer.viewportY >= buffer.baseY;
      // xterm 6 keeps scroll position in its virtual ScrollableElement. The
      // child `.xterm-viewport` is only a layout shell and has no reliable
      // native scrollTop/scrollHeight. The buffer position is the sole
      // scroll authority, so a repaint cannot make us follow the tail after
      // the user has dragged into history.
      const atBottom = bufferAtBottom;
      // Update the ref synchronously with xterm's scroll event. Output can
      // arrive between this event and React's next render; leaving the ref at
      // `true` makes that output call scrollToBottom() and snaps the user's
      // drag back to the tail after only a few rows.
      followRef.current = atBottom;
      saveTerminalViewport(session.id, terminal, atBottom);
      setFollow((current) => current === atBottom ? current : atBottom);
    };
    const scrollDisposable = terminal.onScroll(captureScrollPosition);
    const inputDisposable = terminal.onData((data) => {
      if (!readOnly) writeInput(data);
    });
    const pasteImage = (event: ClipboardEvent) => {
      const image = [...(event.clipboardData?.items ?? [])]
        .find((item) => item.type.startsWith("image/"))
        ?.getAsFile();
      if (!image) return;
      event.preventDefault();
      void stageImage(image);
    };
    // Capture image pastes before xterm's text paste handler turns the
    // clipboard event into terminal bytes.
    host.addEventListener("paste", pasteImage, true);

    emitLifecycle({ type: "connection", state: "connecting" });
    setHistoryLoading(true);
    setError("");
    setControlNotice("idle");
    let disposeAttachedConnection = () => {};
    const attachConnection = async (opened: TerminalConnection) => {
      if (cancelled) {
        opened.close();
        return;
      }
      disposeAttachedConnection();
      attachedConnection = opened;
      connectionRef.current = opened;
      previousControlMode = "unavailable";
      // Remote observers resolve their connection before the first joined
      // snapshot. Wait for that barrier so history cannot race the screen
      // bootstrap and paint the same TUI twice.
      try {
        const initialSnapshotReady = opened.initialSnapshotReady;
        if (initialSnapshotReady) {
          let timeout: number | null = null;
          await Promise.race([
            initialSnapshotReady,
            new Promise<void>((_, reject) => {
              timeout = window.setTimeout(() => reject(new Error(t("terminal.snapshotTimeout"))), INITIAL_SNAPSHOT_TIMEOUT_MS);
            }),
          ]).finally(() => {
            if (timeout !== null) window.clearTimeout(timeout);
          });
        }
      } catch (cause) {
        if (cancelled || terminalRuntime.current !== opened) return;
        opened.close();
        setHistoryLoading(false);
        const message = cause instanceof Error ? cause.message : String(cause);
        setError(message);
        emitLifecycle({ type: "connection", state: "connection_failed" });
        emitLifecycle({ type: "recoverable_error", message });
        return;
      }
      if (cancelled || terminalRuntime.current !== opened) return;
      setHistoryLoading(false);
      if (terminalRuntime.current !== opened) return;
      if (opened.initialSnapshot) {
        applySnapshot(
          opened.initialSnapshot,
          true,
        );
      }
      // The initial snapshot was already applied above. Remote connections
      // may emit another snapshot after an internal observer reconnect; only
      // those marked `reset` should replace the xterm screen.
      unsubscribeSnapshot = opened.onSnapshot((snapshot) => applySnapshot(
        snapshot,
        snapshot.reset === true,
      ));
      unsubscribePrompt = opened.onPrompt((prompt) => onPrompt?.(prompt));
      unsubscribeConnectionClose = opened.onClose(() => {
        if (attachedConnection !== opened) return;
        attachedConnection = null;
        connectionRef.current = null;
      });
      unsubscribeData = opened.onData((data) => {
        const detectedCursorMode = detectApplicationCursorMode(data);
        if (detectedCursorMode !== null) {
          setApplicationCursor(detectedCursorMode);
          if (latestSnapshotRef.current) {
            latestSnapshotRef.current = {
              ...latestSnapshotRef.current,
              applicationCursor: detectedCursorMode,
            };
          }
        }
        if (pausedRef.current) {
          pausedChunksRef.current.push(data);
          pausedBytesRef.current += data.byteLength;
          while (pausedBytesRef.current > MAX_PAUSED_BYTES && pausedChunksRef.current.length > 1) {
            pausedBytesRef.current -= pausedChunksRef.current.shift()?.byteLength ?? 0;
          }
          return;
        }
        queueTerminalWrite(data, () => {
          if (followRef.current) scrollTerminalSurfaceToBottom(terminal);
        });
      });
      unsubscribeControl = opened.onControlState((next) => {
        if (cancelled) return;
        const becameOwned = next.mode === "owned" && previousControlMode !== "owned";
        previousControlMode = next.mode;
        terminal.options.cursorBlink = next.mode === "owned";
        terminal.options.disableStdin = readOnly || next.mode !== "owned";
        setController(next);
        emitLifecycle({ type: "control", state: desktopNativeControlState(next.mode) });
        if (!readOnly && next.mode === "owned") flushPendingInput();
        if (!readOnly && next.mode === "owned") {
          terminal.options.fontSize = DEFAULT_FONT_SIZE;
          terminal.options.lineHeight = DEFAULT_LINE_HEIGHT;
          if (becameOwned) {
            scheduleWritableResize();
            if (!window.matchMedia("(pointer: coarse)").matches) terminal.focus();
          }
        }
      });
      emitLifecycle({ type: "connection", state: "connected" });
      emitLifecycle({ type: "snapshot_ready" });
      // A desktop controller is the only client allowed to negotiate the
      // Session Host grid. Do this after the authoritative snapshot has
      // established xterm's parser dimensions; observers remain local-only.
      if (!readOnly && opened.writable) scheduleWritableResize();
      const controlAction = terminalControlAction(
        readOnly,
        opened.controller.mode,
        pendingInputRef.current.length > 0,
      );
      if (controlAction === "acquire_and_flush") {
        requestControlAndFlush(opened);
      } else if (controlAction === "acquire") {
        // Claim an idle lease automatically, but never steal control from an
        // active client without the explicit takeover button below.
        void opened.requestControl(false).catch(() => {});
      } else if (controlAction === "flush") {
        flushPendingInput();
      }
    };
    disposeAttachedConnection = () => {
      unsubscribeData();
      unsubscribeSnapshot();
      unsubscribePrompt();
      unsubscribeConnectionClose();
      unsubscribeControl();
      unsubscribeData = () => {};
      unsubscribeSnapshot = () => {};
      unsubscribePrompt = () => {};
      unsubscribeConnectionClose = () => {};
      unsubscribeControl = () => {};
      attachedConnection = null;
      connectionRef.current = null;
    };
    const terminalRuntime = new TerminalConnectionRuntime<TerminalConnection>({
      open: async () => {
        const opened = await client.connectTerminal(session.id);
        if (!opened) throw new Error(t("terminal.unavailable"));
        return opened;
      },
      shouldReconnect: async () => {
        try {
          const latest = await client.getSession(session.id);
          return !["idle", "done", "stopped", "exited", "error"].includes(latest.state);
        } catch {
          return true;
        }
      },
      maxReconnectAttempts: MAX_TERMINAL_RECONNECT_ATTEMPTS,
      stableConnectionMs: STABLE_CONNECTION_MS,
      retryingMessage: t("terminal.connecting"),
      retryLimitMessage: t("terminal.unavailable"),
      formatError: (cause) => cause instanceof Error ? cause.message : String(cause),
    });
    connectionRuntimeRef.current = terminalRuntime;
    const unsubscribeRuntime = terminalRuntime.subscribe((next) => {
      if (cancelled) return;
      if (next.phase === "connecting") {
        emitLifecycle({ type: "connection", state: "connecting" });
        setError(next.error ?? "");
      } else if (next.phase === "closed") {
        setHistoryLoading(false);
        emitLifecycle({ type: "connection", state: next.error ? "connection_failed" : "disconnected" });
        if (next.error) emitLifecycle({ type: "recoverable_error", message: next.error });
        setError(next.error ?? "");
      }
    });
    const unsubscribeConnections = terminalRuntime.subscribeConnections((opened) => {
      void attachConnection(opened);
    });
    terminalRuntime.start();

    return () => {
      cancelled = true;
      if (controlNoticeTimerRef.current !== null) {
        window.clearTimeout(controlNoticeTimerRef.current);
        controlNoticeTimerRef.current = null;
      }
      if (resizeTimer !== null) {
        window.clearTimeout(resizeTimer);
        resizeTimer = null;
      }
      window.cancelAnimationFrame(frame);
      if (settledFitFrame !== null) window.cancelAnimationFrame(settledFitFrame);
      settledFitFrame = null;
      cancelScheduledProjection();
      resizeObserver.disconnect();
      scrollDisposable.dispose();
      inputDisposable.dispose();
      host.removeEventListener("paste", pasteImage, true);
      unsubscribeConnections();
      unsubscribeRuntime();
      terminalRuntime.dispose();
      if (connectionRuntimeRef.current === terminalRuntime) connectionRuntimeRef.current = null;
      disposeAttachedConnection();
      enqueueTerminalWriteRef.current = () => undefined;
      saveTerminalViewport(session.id, terminal, followRef.current);
      controlRequestRef.current = null;
      connectionRef.current = null;
      terminalRef.current = null;
      canonicalTerminalRef.current = null;
      latestSnapshotRef.current = null;
      terminal.dispose();
      canonical.dispose();
      if (attachmentPreviewRef.current) URL.revokeObjectURL(attachmentPreviewRef.current);
      attachmentPreviewRef.current = null;
      pausedChunksRef.current = [];
      pausedBytesRef.current = 0;
    };
  }, [client, onPrompt, readOnly, session.generation, session.id, session.processAttemptId]);

  const retryNow = () => {
    connectionRuntimeRef.current?.retry();
  };

  function flushPendingInput(): void {
    const connection = connectionRef.current;
    if (!connection?.writable) return;
    while (pendingInputRef.current.length) {
      const bytes = pendingInputRef.current[0];
      try {
        connection.write(bytes);
        pendingInputRef.current.shift();
        pendingInputBytesRef.current = Math.max(
          0,
          pendingInputBytesRef.current - bytes.byteLength,
        );
      } catch (cause) {
        setInputError(localizeError(cause, t, "terminal.inputFailed"));
        break;
      }
    }
    if (!pendingInputRef.current.length) setInputError(null);
  }

  function enqueueInput(bytes: Uint8Array): void {
    pendingInputRef.current.push(bytes);
    pendingInputBytesRef.current += bytes.byteLength;
    while (
      pendingInputBytesRef.current > MAX_PENDING_INPUT_BYTES &&
      pendingInputRef.current.length > 1
    ) {
      pendingInputBytesRef.current -= pendingInputRef.current.shift()?.byteLength ?? 0;
    }
  }

  function showControlAcquired(): void {
    if (controlNoticeTimerRef.current !== null) {
      window.clearTimeout(controlNoticeTimerRef.current);
    }
    setControlNotice("acquired");
    controlNoticeTimerRef.current = window.setTimeout(() => {
      controlNoticeTimerRef.current = null;
      setControlNotice("idle");
    }, 1_500);
  }

  function requestControlAndFlush(connection: TerminalConnection, force = false): void {
    if (controlRequestRef.current) return;
    setControlNotice("acquiring");
    const request = connection
      .requestControl(force)
      .then((state) => {
        if (state.mode !== "owned") {
          setControlNotice("idle");
          return;
        }
        flushPendingInput();
        showControlAcquired();
      })
      .catch((cause) => {
        // Keep queued bytes across a transient control/relay failure. The
        // next connection or control-state event will retry them.
        setControlNotice("idle");
        setInputError(localizeError(cause, t, "terminal.inputFailed"));
      })
      .finally(() => {
        if (controlRequestRef.current === request) controlRequestRef.current = null;
      });
    controlRequestRef.current = request;
  }

  function writeInput(data: string): void {
    const connection = connectionRef.current;
    if (readOnly) return;
    const bytes = inputEncoder.encode(data);
    if (!bytes.byteLength) return;
    if (!connection) {
      enqueueInput(bytes);
      setInputError(t("terminal.connecting"));
      return;
    }
    if (!connection.writable) {
      if (controller.mode === "held-elsewhere") {
        setInputError(t("terminal.controlHeldElsewhere"));
        return;
      }
      enqueueInput(bytes);
      requestControlAndFlush(connection);
      return;
    }
    try {
      connection.write(bytes);
      setInputError(null);
    } catch (cause) {
      enqueueInput(bytes);
      setInputError(localizeError(cause, t, "terminal.inputFailed"));
    }
  }

  async function stageImage(file: File): Promise<void> {
    setAttachmentError(null);
    if (!/^image\/(?:png|jpeg|webp)$/i.test(file.type)) {
      setAttachmentError(t("terminal.imageTypeUnsupported"));
      return;
    }
    if (file.size === 0 || file.size > 10 * 1024 * 1024) {
      setAttachmentError(t("terminal.imageTooLarge"));
      return;
    }
    const connection = connectionRef.current;
    if (!connection || typeof connection.uploadImage !== "function") {
      setAttachmentError(t("terminal.imageUnavailable"));
      return;
    }
    if (attachmentPreviewRef.current) URL.revokeObjectURL(attachmentPreviewRef.current);
    const previewUrl = URL.createObjectURL(file);
    attachmentPreviewRef.current = previewUrl;
    setTerminalAttachment({ fileName: file.name || "image", previewUrl, path: null });
    setAttachmentUploading(true);
    setAttachmentProgress(0);
    try {
      const result = await connection.uploadImage(
        { fileName: file.name || "image", mimeType: file.type, bytes: new Uint8Array(await file.arrayBuffer()) },
        (uploadedBytes, totalBytes) => setAttachmentProgress(totalBytes ? Math.round(uploadedBytes / totalBytes * 100) : 0),
      );
      setTerminalAttachment((current) => current ? { ...current, path: result.path } : current);
      // Inserting the returned local path uses the same xterm input path as
      // keyboard paste. It intentionally does not press Enter.
      terminalRef.current?.paste(result.path);
      terminalRef.current?.focus();
      setAttachmentProgress(100);
    } catch (cause) {
      setAttachmentError(localizeError(cause, t, "terminal.imageUnavailable"));
      URL.revokeObjectURL(previewUrl);
      attachmentPreviewRef.current = null;
      setTerminalAttachment(null);
      setAttachmentProgress(0);
    } finally {
      setAttachmentUploading(false);
    }
  }

  const clearAttachment = () => {
    if (attachmentPreviewRef.current) URL.revokeObjectURL(attachmentPreviewRef.current);
    attachmentPreviewRef.current = null;
    setTerminalAttachment(null);
    setAttachmentError(null);
    setAttachmentProgress(0);
    if (attachmentInputRef.current) attachmentInputRef.current.value = "";
  };

  useEffect(() => {
    if (!insertText || readOnly || lastInsertNonceRef.current === insertText.nonce) return;
    let cancelled = false;
    const insert = async () => {
      const connection = connectionRef.current;
      const terminal = terminalRef.current;
      if (!terminal) {
        setInputError(t("terminal.unavailable"));
        return;
      }
      try {
        if (connection && controller.mode !== "owned") {
          const state = await connection.requestControl(false);
          if (state.mode !== "owned") throw new Error(t("terminal.controlHeldElsewhere"));
        }
        if (cancelled) return;
        // xterm paste honors the Provider's bracketed-paste mode, so multiline
        // note text is inserted as text and never interpreted as Enter.
        terminal.paste(insertText.text);
        lastInsertNonceRef.current = insertText.nonce;
        terminal.focus();
        setInputError(null);
      } catch (cause) {
        if (!cancelled) setInputError(localizeError(cause, t, "terminal.inputFailed"));
      }
    };
    void insert();
    return () => { cancelled = true; };
  }, [controller.mode, insertText, readOnly, status, t]);

  useEffect(() => {
    if (!promptAnswer || readOnly) return;
    const connection = connectionRef.current;
    if (!connection) return;
    let cancelled = false;
    const answer = async () => {
      try {
        if (!connection.writable) {
          const state = await connection.requestControl(false);
          if (state.mode !== "owned") throw new Error(t("terminal.controlHeldElsewhere"));
        }
        if (cancelled) return;
        connection.answerPrompt(promptAnswer.promptId, promptAnswer.optionId);
        setInputError(null);
        onPrompt?.(null);
      } catch (cause) {
        if (!cancelled) setInputError(localizeError(cause, t, "terminal.promptFailed"));
      }
    };
    void answer();
    return () => { cancelled = true; };
  }, [onPrompt, promptAnswer, readOnly, t]);

  const sendQuickCommand = (quickCommand: ProviderTerminalQuickCommand) => {
    writeInput(quickCommand.command);
    // The Provider composer receives text and Enter as separate PTY frames.
    // This matters for Claude's alternate-screen composer and avoids a pasted
    // command being displayed without being submitted.
    if (quickCommand.execution === "submit" || quickCommand.execution === "interactive") {
      window.setTimeout(() => writeInput("\r"), 80);
    }
    setQuickCommandsOpen(false);
  };

  const quickCommands = nativeQuickCommands;

  const copySelection = async () => {
    const selection = terminalRef.current?.getSelection() ?? "";
    if (selection) await navigator.clipboard?.writeText(selection);
  };

  return <section className={`terminal-surface ${minimalChrome ? "minimal-chrome" : ""}`} aria-label={t("terminal.aria")}>
    <div className="terminal-stage">
      <div
        className="terminal-host"
        ref={hostRef}
        onClick={() => {
          // A terminal tap is display interaction only. Control is acquired by
          // the explicit bottom action (or by the initial free-lease attach),
          // so clicking an observer cannot repeatedly race another controller.
          if (!readOnly && connectionRef.current?.writable) terminalRef.current?.focus();
        }}
      />
    </div>
    {!minimalChrome ? <div className={"terminal-floating-tools " + (toolsExpanded ? "expanded" : "collapsed")}>
      <button className="session-tools-toggle" type="button" onClick={onToggleTools} title={toolsExpanded ? t("session.tools.collapse") : t("session.tools.expand")} aria-label={toolsExpanded ? t("session.tools.collapse") : t("session.tools.expand")}>{toolsExpanded ? <PanelRightClose size={14} /> : <PanelRightOpen size={14} />}</button>
      {toolsExpanded ? <>
      <button type="button" onClick={onOpenNotes} title={t("notes.title")}><Lightbulb size={14} /></button>
      <button className={paused ? "active" : ""} type="button" onClick={() => setPaused((value) => !value)} title={paused ? t("terminal.outputResume") : t("terminal.outputPause")}>{paused ? <Play size={14} /> : <Pause size={14} />}</button>
      <button className={follow ? "active" : ""} type="button" onClick={() => { setFollow(true); terminalRef.current?.scrollToBottom(); }} title={t("terminal.follow")}><ArrowDown size={14} /></button>
      <button className={status === "live" ? "online" : ""} type="button" title={status === "live" ? t("terminal.online") : t("terminal.reconnect")} onClick={retryNow}><RotateCcw size={14} /></button></> : null}
    </div> : null}
    {status === "connecting" ? <div className="terminal-state-overlay terminal-state-connecting" role="status" aria-live="polite">
      <span className="terminal-state-icon"><span className="spinner" /></span>
      <strong>{t("terminal.connecting")}</strong>
    </div> : null}
    {controlNotice !== "idle" ? (
      <div className={`terminal-control-notice ${controlNotice}`} role="status" aria-live="polite">
        {controlNotice === "acquiring"
          ? <><span className="spinner" />{t("terminal.controlAcquiring")}</>
          : t("terminal.controlAcquired")}
      </div>
    ) : null}
    {!readOnly && status === "live" && controller.mode !== "owned" ? (
      <button
        type="button"
        className="terminal-control-takeover"
        onClick={() => {
          const connection = connectionRef.current;
          if (connection) requestControlAndFlush(connection, true);
        }}
        disabled={controlRequestRef.current !== null}
      >{t("terminal.controlTakeover")}</button>
    ) : null}
    {status === "closed" || status === "error" ? <div className="terminal-state-overlay terminal-state-error" role="alert" aria-live="polite">
      <span className="terminal-state-icon"><RotateCcw size={20} /></span>
      <strong>{error || t("terminal.disconnected")}</strong>
      <button type="button" onClick={retryNow}><RotateCcw size={15} />{t("terminal.reconnectNow")}</button>
    </div> : null}
    {inputError ? <div className="terminal-input-error">{inputError}</div> : null}
    {!minimalChrome && !readOnly ? <div className="terminal-lower-tools">
      <div className="terminal-quick-commands">
        {quickCommandsOpen ? <div className="terminal-quick-commands-panel" role="menu" aria-label={t("terminal.quick.title")}><header>{t("terminal.quick.title")}</header>{quickCommands.map((command) => <button type="button" key={command.command} role="menuitem" onClick={() => sendQuickCommand(command)}>{command.command}</button>)}</div> : null}
        <button type="button" className={`terminal-quick-commands-trigger ${quickCommandsOpen ? "active" : ""}`} onClick={() => setQuickCommandsOpen((value) => !value)} title={t("terminal.quick.title")} aria-label={t("terminal.quick.title")} aria-expanded={quickCommandsOpen}><Command size={15} /></button>
      </div>
      <label className="terminal-image-trigger" title={t("terminal.attachImage")} aria-label={t("terminal.attachImage")}>
        <ImagePlus size={15} />
        <input
          ref={attachmentInputRef}
          type="file"
          accept="image/png,image/jpeg,image/webp"
          disabled={attachmentUploading}
          onChange={(event) => {
            const file = event.currentTarget.files?.[0];
            if (file) void stageImage(file);
            event.currentTarget.value = "";
          }}
        />
      </label>
      <div className={`terminal-keypad ${keypadOpen ? "open" : ""}`}>
      {keypadOpen ? <div className="terminal-keypad-panel">
        <button type="button" onClick={() => void copySelection()} title={t("terminal.copySelection")}><Copy size={13} /></button>
        <button type="button" onClick={() => writeInput("/")}>/</button>
        <button type="button" onClick={() => writeInput("\u001b")}>Esc</button>
        <button type="button" onClick={() => writeInput("\t")}>Tab</button>
        <button type="button" onClick={() => writeInput(terminalArrowSequence("up", applicationCursor))} title={t("terminal.up")}><ArrowUp size={13} /></button>
        <button type="button" onClick={() => writeInput(terminalArrowSequence("down", applicationCursor))} title={t("terminal.down")}><ArrowDown size={13} /></button>
        <button type="button" onClick={() => writeInput("\r")} title={t("common.enter")}><CornerDownLeft size={13} /></button>
        <button type="button" className="terminal-keypad-toggle" onClick={() => setKeypadOpen(false)} title={t("terminal.keysCollapse")}><Keyboard size={13} /></button>
        </div> : <button type="button" className="terminal-keypad-trigger" onClick={() => setKeypadOpen(true)} title={t("terminal.keysShow")}><Keyboard size={15} /></button>}
      </div>
    </div> : null}
    {terminalAttachment ? <div className="terminal-image-attachment" role="status">
      <img src={terminalAttachment.previewUrl} alt={terminalAttachment.fileName} />
      <span>{terminalAttachment.path ? t("terminal.imageReady") : `${t("terminal.imageUploading")} ${attachmentProgress}%`}</span>
      <button type="button" onClick={clearAttachment} disabled={attachmentUploading} title={t("terminal.removeImage")} aria-label={t("terminal.removeImage")}><X size={12} /></button>
    </div> : null}
    {attachmentError ? <div className="terminal-input-error" role="alert">{attachmentError}</div> : null}
    {historyLoading && status === "live" ? <div className="terminal-history-loading"><span className="spinner" />{t("terminal.historyLoading")}</div> : null}
  </section>;
}

function desktopNativeSessionStatus(state: Session["state"]): "starting" | "running" | "waiting" | "stopping" | "stopped" | "exited" | "error" {
  // The Desktop domain still exposes the retired `idle`/`done` labels for
  // old projections. Native Terminal treats both as an inactive process and
  // never lets them masquerade as a live connection.
  if (state === "idle" || state === "done") return "stopped";
  return state;
}

function desktopTerminalStatus(
  lifecycle: ReturnType<typeof nativeTerminalLifecycleFromSession>,
): "connecting" | "live" | "closed" | "error" {
  if (lifecycle.connection === "connecting" || lifecycle.connection === "reconnecting") return "connecting";
  if (lifecycle.connection === "connection_failed" || lifecycle.recoverableError) return "error";
  if (lifecycle.connection === "connected" && lifecycle.screenReady) return "live";
  return lifecycle.process === "stopped" || lifecycle.process === "exited" ? "closed" : "connecting";
}

function desktopNativeControlState(
  mode: ControllerState["mode"],
): "none" | "acquiring" | "owned" | "held_by_other" | "expired" | "rejected" {
  switch (mode) {
    case "owned": return "owned";
    case "held-elsewhere": return "held_by_other";
    case "available": return "none";
    case "unavailable": return "rejected";
  }
}

/** Fit canonical columns to this observer without changing the shared PTY. */
function fitTerminalGlyphsToHost(host: HTMLElement, terminal: Terminal): void {
  if (host.clientWidth < 20 || host.clientHeight < 20 || terminal.cols < 1 || terminal.rows < 1) return;
  const screen = host.querySelector<HTMLElement>(".xterm-screen, .xterm-rows");
  if (!screen) return;
  const measuredWidth = screen.getBoundingClientRect().width;
  const currentFontSize = terminal.options.fontSize ?? DEFAULT_FONT_SIZE;
  if (measuredWidth <= 1 || currentFontSize <= 0) return;
  // Keep the canonical parser grid intact, but use the observer's real width
  // for glyph sizing. The old cap of 18px left wide desktop windows using
  // only part of the screen and made text wrap at the phone-sized grid.
  const targetFontSize = Math.round(currentFontSize * host.clientWidth / measuredWidth);
  const nextFontSize = Math.min(28, Math.max(8, targetFontSize));
  const availableHeight = Math.max(1, host.clientHeight);
  const targetLineHeight = availableHeight / Math.max(1, terminal.rows * nextFontSize);
  const nextLineHeight = Math.min(2.2, Math.max(DEFAULT_LINE_HEIGHT, targetLineHeight));
  if (nextFontSize !== currentFontSize) {
    terminal.options.fontSize = nextFontSize;
  }
  if (terminal.options.lineHeight !== nextLineHeight) {
    terminal.options.lineHeight = nextLineHeight;
  }
}

function scrollTerminalSurfaceToBottom(terminal: Terminal): void {
  terminal.scrollToBottom();
}

function terminalArrowSequence(direction: "left" | "right" | "up" | "down", applicationCursor: boolean): string {
  const suffix = { left: "D", right: "C", up: "A", down: "B" }[direction];
  return `\u001b${applicationCursor ? "O" : "["}${suffix}`;
}

function detectApplicationCursorMode(bytes: Uint8Array): boolean | null {
  if (bytes.byteLength === 0) return null;
  const text = new TextDecoder().decode(bytes);
  let mode: boolean | null = null;
  const pattern = /\u001b\[\?([0-9;]+)([hl])/gu;
  for (const match of text.matchAll(pattern)) {
    if (match[1]?.split(";").includes("1")) mode = match[2] === "h";
  }
  return mode;
}
